// HER DASHBOARD ITEM, SURFACE 2(c) — **THE FULL CONVERSION LADDER, ONE HONEST ROW.**
//
// Her words: *visitors, signups, paid, returning, as counts AND percentages for the period,
// **RECONCILED: the signups number must MATCH Overview's signups for the same period.** (She caught
// 42 in one place meaning something else elsewhere.) One truth everywhere, the same Pacific period
// logic.*
//
// >>> THE RECONCILIATION IS NOT A COMPARISON, IT IS A CONSOLIDATION, AND THAT IS THE ROUND. <<<
// A clause asserting *this number equals that number* is satisfied by two hand-typed predicates that
// happen to agree today, which is exactly the state she caught: measured before a line was written,
// *a signup in this window* was spelled **THREE TIMES** in `server/db/command-center.js` — the
// Overview tile, the growth series and the prior-period delta — and nothing anywhere made them one
// answer. `SIGNUP_IN_WINDOW_SQL` is that declaration now, and this ladder reads it too. **Four
// spellings of one law is four answer keys, and the one that gets missed is the one somebody adds
// next** (L16's own finding, on the number she runs the business on).
//
// **IT IS A SHARED `.js` LAW RATHER THAN `src/lib/*.ts` FOR `strings.js`' OWN REASON:** the SERVER
// composes this row's counts and the CLIENT draws it, and `src/lib` is client-only. It imports
// NOTHING, which is the entry requirement that lets a `.mjs` gate load and RUN every line of it.

// ---------------------------------------------------------------------------
// THE FOUR STEPS, IN HER ORDER, DECLARED ONCE.
//
// The ORDER is the whole point of a ladder: each row is a share of the one above it, so a roster in
// any other order is a set of four numbers rather than a journey. `returning` is the exception and
// is declared as one below.
// ---------------------------------------------------------------------------
export const LADDER_STEPS = ["visitors", "signups", "paid", "returning"];

// What she reads each step called. **Never a machine key** (S1-A/R117's law binds on any owner-facing
// line), and never a word that means something else on another screen: *Became members* is the funnel
// report card's own wording for the same question one denominator down, so the two cards cannot say
// two things about one act.
export const LADDER_LABELS = {
  visitors: "Visitors",
  signups: "Signed up",
  paid: "Became members",
  returning: "Came back",
};

// >>> WHICH NUMBER EACH STEP IS A SHARE OF — AND `visitors` IS A SHARE OF NOTHING, WHICH IS A FACT
// RATHER THAN A MISSING ENTRY. <<< It is the top of the ladder: a percentage beside it could only
// ever be 100%, which is a number that looks like information and is not.
export const LADDER_DENOMINATOR = {
  visitors: null,
  signups: "visitors",
  paid: "signups",
  // **RETURNING IS A SHARE OF VISITORS AND NOT OF THE STEP ABOVE IT**, because it is not a rung of
  // the same journey: a member who came back this month did not sign up or pay this month. It is
  // here because her clause names it, and it is read against the population it is really part of.
  returning: "visitors",
};

const num = (v) => (typeof v === "number" && Number.isFinite(v) && v >= 0 ? Math.floor(v) : null);

/**
 * **THE ROW SHE READS. COUNTS AND PERCENTAGES, AND EVERY ONE OF THEM ALLOWED TO BE UNKNOWN.**
 *
 * >>> `null` IS NOT ZERO ANYWHERE ON THIS ROW, AND THAT IS R18 ON THE ONE LINE THAT SUMMARISES HER
 * WHOLE BUSINESS. <<< A traffic read that refused answers `visitors: null`, and rendering that as
 * `0` puts *nobody came to your site* on her screen on a day the database was busy — a number she
 * would act on. A count that could not be read and a count that is really zero are different facts,
 * and only the second one is news.
 *
 * >>> AND A PERCENTAGE IS WITHHELD WHENEVER ITS DENOMINATOR CANNOT CARRY IT. <<< Three ways that
 * happens, and all three are the same ruling (R141: no estimate dressed as a count):
 *   1. the denominator is UNKNOWN — nothing to divide by;
 *   2. the denominator is ZERO — *nobody converted* and *nobody arrived* are different facts, and
 *      only the first is a problem;
 *   3. the visit ledger does not COVER this window (`coverage !== "measured"`) — the collector
 *      records forward only, so a window that begins before it started divides real signups by a
 *      denominator that does not contain them. That rate reads well over 100% where it shows and
 *      **flatters her everywhere else**, on the row she judges her advertising by.
 *
 * The coverage test binds only on steps whose denominator is `visitors`, because that is the only
 * number the collector supplies. `paid / signups` comes off one table over one window and is exact
 * whatever the traffic ledger knows.
 */
export function conversionLadderRows(ladder = {}) {
  const counts = {
    visitors: num(ladder.visitors),
    signups: num(ladder.signups),
    paid: num(ladder.paid),
    returning: num(ladder.returning),
  };
  const covered = ladder.coverage === "measured";
  return LADDER_STEPS.map((key) => {
    const of = LADDER_DENOMINATOR[key];
    const whole = of ? counts[of] : null;
    const part = counts[key];
    const usable = of !== null
      && (of !== "visitors" || covered)
      && typeof whole === "number" && whole > 0
      && typeof part === "number";
    return {
      key,
      label: LADDER_LABELS[key],
      count: part,
      of,
      pct: usable ? Math.round((part / whole) * 100) : null,
    };
  });
}

/**
 * **THE ONE SENTENCE UNDER THE ROW, AND IT EXISTS BECAUSE A WITHHELD PERCENTAGE LOOKS LIKE A BROKEN
 * ONE.** Four numbers with two blank shares is a screen she would report as broken; the same four
 * with a line saying *visits have only been counted since the 14th* is a screen she can act on.
 *
 * THREE OUTCOMES, NEVER TWO (R18), and `measured` says NOTHING — a screen that keeps apologising
 * after the gap has closed is one she stops reading (SURFACE 2(b)'s own ruling, one card up).
 */
export function ladderCoverageNote(coverage, since = "") {
  if (coverage === "measured") return "";
  if (coverage === "partial") {
    const day = String(since || "").slice(0, 10);
    return day
      ? `Visitors have only been counted since ${day}, which is inside this window, so the shares of visitors are not shown.`
      : "Visitors are only partly counted for this window, so the shares of visitors are not shown.";
  }
  return "Visitors are not counted yet, so the shares of visitors are not shown.";
}
